import ResizeObserver from "resize-observer-polyfill";
import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
// Set up axios interceptors for global error handling (must be imported early)
import "./utils/axiosSetup.js";
import App from "./App.js";
import { AuthProvider } from "./utils/AuthContext.js";
import { ThemeProvider } from "@mui/material";
import { Theme } from "./styles/Theme.js";
import { SnackbarProvider, Snackbar } from "src/utils/SnackbarContext.js";
import { ErrorBoundary, ErrorComponent, rollbar } from "./utils/ErrorBoundary/index.js";
import StripeWrapper from "./components/StripeWrapper.js";
import { SIGN_IN_REDIRECT_PARAMS_KEY } from "./utils/firebaseSignIn.js";
import { reportWebVitals } from "./utils/webVitals.js";
import { captureUtmAttribution } from "./utils/attribution.js";
import { initImageCache, warmCaches } from "./utils/imageCacheStorage.js";
import { registerImageWorker } from "./utils/registerImageWorker.js";
import { HelmetProvider } from "react-helmet-async";
import {
  installIntersectionObserverFallback,
  installScrollOptionsFallback,
  installTranslationDomGuard,
} from "./utils/browserCompat.js";

if (!window.ResizeObserver) {
  window.ResizeObserver = ResizeObserver;
}

installIntersectionObserverFallback();
installTranslationDomGuard({
  onMismatch: (message) =>
    rollbar.warning(`[browserCompat] ${message}`, {
      htmlClass: document.documentElement.className,
      htmlLang: document.documentElement.lang,
    }),
});
installScrollOptionsFallback();

// Suppress benign ResizeObserver error that the dev overlay treats as a crash
window.addEventListener("error", (e) => {
  if (e.message?.includes("ResizeObserver loop")) {
    e.stopImmediatePropagation();
  }
});

// Capture last-touch UTM attribution from the landing URL
captureUtmAttribution();

// Discard image caches from older versions and reclaim space when idle
initImageCache();

// Open the image caches before the first tile asks for art, so no single tile
// pays the cache store's first-access cost. Not awaited: it must not delay the
// shell's first paint.
warmCaches();

// Lets already-processed art be served on the browser's own image path
registerImageWorker();

// Restore signIn query parameters if returning from a redirect
const savedSignInParams = sessionStorage.getItem(SIGN_IN_REDIRECT_PARAMS_KEY);
if (savedSignInParams) {
  sessionStorage.removeItem(SIGN_IN_REDIRECT_PARAMS_KEY);
  try {
    const currentParams = new URLSearchParams(window.location.search);
    const savedParamsObj = new URLSearchParams(savedSignInParams);
    for (const [key, value] of savedParamsObj.entries()) {
      if (!currentParams.has(key)) {
        currentParams.set(key, value);
      }
    }
    const newSearch = currentParams.toString() ? `?${currentParams.toString()}` : "";
    window.history.replaceState({}, "", window.location.pathname + newSearch + window.location.hash);
  } catch (e) {
    console.warn("Failed to restore signInRedirectParams", e);
  }
}

const root = ReactDOM.createRoot(document.getElementById("root"));

root.render(
  <HelmetProvider>
    <ThemeProvider theme={Theme}>
      <ErrorBoundary fallback={<ErrorComponent />}>
        <SnackbarProvider>
          <AuthProvider>
            <StripeWrapper>
              <Snackbar />
              <App />
            </StripeWrapper>
          </AuthProvider>
        </SnackbarProvider>
      </ErrorBoundary>
    </ThemeProvider>
  </HelmetProvider>
);

reportWebVitals();
